import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  fetchReferential,
  fetchSkillsByGroup,
  fetchGroupsByPosition,
  searchSkillPositions,
  quickCreateSkill,
  quickCreateGroup,
  quickCreatePosition,
  quickCreateSector,
  quickCreateSubsector,
  uploadSectorAsset,
  slugify,
  restoreReferentialItem,
  moveReferentialNode,
  updateReferentialItem,
  reorderReferentialItems,
  fetchPositionCertifications,
  linkPositionCertification,
  unlinkPositionCertification,
  runMissingTranslations,
  updateTranslation,
  toggleTranslationLock,
  TRANSLATION_LANGS,
  translationEditsFromItem,
  type RefItem,
  type RefTable,
  type PositionCertification,
} from '../lib/referentialService';
import DeprecateDialog from '../components/DeprecateDialog';
import ImportWizard from '../components/ImportWizard';
import MissingTranslationsModal from '../components/MissingTranslationsModal';
import { logger } from '../lib/logger';

// ─── Types ───────────────────────────────────────────────────────────────

interface PositionDetail {
  groups: RefItem[];
  skillsByGroup: Record<string, RefItem[]>;
}

/** Cible d'insertion d'un poste. `afterId` vaut 'top' ou l'id du poste précédent. */
interface AddPositionTarget {
  sectorId: string;
  subsectorId?: string;
  afterId: string;
}

type CreateModal =
  | { type: 'sector' }
  | { type: 'subsector'; sectorId: string };

/** Les cinq niveaux éditables de l'arbre. */
type NodeKind = 'sector' | 'subsector' | 'position' | 'group' | 'skill';

const NODE_TABLE: Record<NodeKind, RefTable> = {
  sector: 'sectors',
  subsector: 'subsectors',
  position: 'positions',
  group: 'skill_groups',
  skill: 'skills',
};

const NODE_LABEL: Record<NodeKind, string> = {
  sector: 'le secteur',
  subsector: 'le sous-secteur',
  position: 'le poste',
  group: 'le groupe',
  skill: 'la compétence',
};

/** Modale d'édition unifiée : nom, code, parent, pôle. */
interface EditState {
  kind: NodeKind;
  item: RefItem;
  nameFr: string;
  code: string;
  /** Pôle d'un sous-secteur. */
  category: string;
  /** Parent sélectionné, selon le niveau. */
  sectorId: string;
  subsectorId: string;
  positionId: string;
  groupId: string;
  saving: boolean;
}

/** Élément visé par la modale d'obsolescence. */
interface DeprecateState {
  kind: NodeKind;
  item: RefItem;
}

// ─── Reorder helper ──────────────────────────────────────────────────────

interface Reordered<T> {
  list: T[];
  changes: { id: string; sortOrder: number }[];
}

/**
 * Échange l'élément avec son voisin dans la direction donnée et renumérote
 * toute la fratrie de 0 à n. La renumérotation est nécessaire car beaucoup
 * d'éléments partagent un sort_order à 0 après import.
 */
function moveInList<T extends { id: string; sortOrder: number }>(
  list: T[],
  id: string,
  dir: -1 | 1,
): Reordered<T> | null {
  const from = list.findIndex((i) => i.id === id);
  const to = from + dir;
  if (from === -1 || to < 0 || to >= list.length) return null;

  const swapped = [...list];
  [swapped[from], swapped[to]] = [swapped[to], swapped[from]];

  const next = swapped.map((item, index) => ({ ...item, sortOrder: index }));
  const changes = next
    .filter((item) => list.find((o) => o.id === item.id)!.sortOrder !== item.sortOrder)
    .map((item) => ({ id: item.id, sortOrder: item.sortOrder }));

  return { list: next, changes };
}

/**
 * Rend un conteneur cliquable utilisable au clavier. Nécessaire car ces lignes
 * imbriquent des boutons et ne peuvent donc pas être des <button>.
 */
function clickableRow(onActivate: () => void) {
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

// ─── Move buttons ────────────────────────────────────────────────────────

interface MoveButtonsProps {
  onUp: () => void;
  onDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  orientation?: 'vertical' | 'horizontal';
}

/** À placer dans un parent portant la classe `group/item`. */
const MoveButtons: React.FC<MoveButtonsProps> = ({ onUp, onDown, isFirst, isLast, orientation = 'vertical' }) => {
  const base = 'px-1 leading-none text-gray-400 hover:text-[#00244F] disabled:opacity-0 transition-colors';
  return (
    <span
      className="flex items-center flex-shrink-0 text-[11px] opacity-0 group-hover/item:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onUp(); }}
        disabled={isFirst}
        title="Déplacer avant"
        className={base}
      >
        {orientation === 'vertical' ? '↑' : '‹'}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDown(); }}
        disabled={isLast}
        title="Déplacer après"
        className={base}
      >
        {orientation === 'vertical' ? '↓' : '›'}
      </button>
    </span>
  );
};

// ─── Node actions ────────────────────────────────────────────────────────

interface NodeActionsProps {
  active: boolean;
  onEdit: () => void;
  onDeprecate: () => void;
  onRestore: () => void;
  /** Version resserrée pour les chips de compétence. */
  compact?: boolean;
}

/** Éditer / rendre obsolète / restaurer. À placer dans un parent `group/item`. */
const NodeActions: React.FC<NodeActionsProps> = ({ active, onEdit, onDeprecate, onRestore, compact }) => {
  const base = `${compact ? 'px-0.5 text-[10px]' : 'p-1 text-xs'} leading-none transition-colors`;
  const stop = (fn: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn(); };
  return (
    <span
      className="flex items-center flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
      onClick={(e) => e.stopPropagation()}
    >
      <button type="button" onClick={stop(onEdit)} title="Modifier" className={`${base} text-gray-400 hover:text-blue-600`}>
        ✏️
      </button>
      {active ? (
        <button type="button" onClick={stop(onDeprecate)} title="Rendre obsolète" className={`${base} text-gray-400 hover:text-amber-600`}>
          🚫
        </button>
      ) : (
        <button type="button" onClick={stop(onRestore)} title="Restaurer" className={`${base} text-gray-400 hover:text-green-600`}>
          ↩️
        </button>
      )}
    </span>
  );
};

const ObsoleteBadge: React.FC = () => (
  <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded bg-amber-100 text-amber-700 flex-shrink-0">
    Obsolète
  </span>
);

// ─── Insert Point Component ──────────────────────────────────────────────

interface InsertPointProps {
  active: boolean;
  value: string;
  onChange: (v: string) => void;
  onInsert: () => void;
  onCancel: () => void;
  onActivate: () => void;
  indent: string;
}

const InsertPoint: React.FC<InsertPointProps> = ({
  active,
  value,
  onChange,
  onInsert,
  onCancel,
  onActivate,
  indent,
}) => {
  if (active) {
    return (
      <div className={`px-4 ${indent} py-1`}>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onInsert();
            if (e.key === 'Escape') onCancel();
          }}
          onBlur={() => { if (!value.trim()) onCancel(); }}
          placeholder="Nom du poste + Entrée"
          autoFocus
          className="w-full px-2 py-1.5 text-sm border border-[#00244F]/30 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-white"
        />
      </div>
    );
  }

  return (
    <div
      className={`px-4 ${indent} py-0.5 group/insert cursor-pointer`}
      onClick={onActivate}
      title="Insérer un poste ici"
    >
      <div className="h-2 relative flex items-center justify-center opacity-0 group-hover/insert:opacity-100 transition-opacity">
        <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-[#00244F]/40" />
        <span className="relative bg-gray-50 px-1.5 text-[10px] text-[#00244F]/70 font-semibold whitespace-nowrap">
          + poste
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────

const Referentials: React.FC = () => {
  const [sectors, setSectors] = useState<RefItem[]>([]);
  const [subsectors, setSubsectors] = useState<RefItem[]>([]);
  const [positions, setPositions] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());
  const [expandedSubsectors, setExpandedSubsectors] = useState<Set<string>>(new Set());
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Recherche globale : postes contenant une compétence qui matche la requête
  const [skillMatchPositionIds, setSkillMatchPositionIds] = useState<Set<string> | null>(null);

  const [positionDetail, setPositionDetail] = useState<PositionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Inline add states
  const [addPosTarget, setAddPosTarget] = useState<AddPositionTarget | null>(null);
  const [newPositionName, setNewPositionName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingSkillInGroup, setAddingSkillInGroup] = useState<string | null>(null);
  const [newSkillName, setNewSkillName] = useState('');

  // Create sector / subsector
  const [createModal, setCreateModal] = useState<CreateModal | null>(null);
  const [createName, setCreateName] = useState('');
  const [createCode, setCreateCode] = useState('');
  const [createCodeDirty, setCreateCodeDirty] = useState(false);
  const [createCategory, setCreateCategory] = useState('');
  const [createSubName, setCreateSubName] = useState('');
  const [createLogoFile, setCreateLogoFile] = useState<File | null>(null);
  const [createLogoPreview, setCreateLogoPreview] = useState<string | null>(null);
  const [createPhotoFile, setCreatePhotoFile] = useState<File | null>(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);

  // Edit / obsolescence
  const [edit, setEdit] = useState<EditState | null>(null);
  /** Groupes du poste sélectionné dans la modale, quand on déplace une compétence. */
  const [editGroupOptions, setEditGroupOptions] = useState<RefItem[]>([]);
  const [deprecate, setDeprecate] = useState<DeprecateState | null>(null);
  const [showObsolete, setShowObsolete] = useState(false);

  // Certifications rattachées au poste sélectionné
  const [certifications, setCertifications] = useState<RefItem[]>([]);
  const [positionCerts, setPositionCerts] = useState<PositionCertification[]>([]);
  const [addingCert, setAddingCert] = useState(false);

  // Translation
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState('');
  const [showMissingTranslations, setShowMissingTranslations] = useState(false);
  const [translationItem, setTranslationItem] = useState<{ item: RefItem; table: RefTable } | null>(null);
  const [translationEdits, setTranslationEdits] = useState<Record<string, string>>({});
  const [translationSaving, setTranslationSaving] = useState(false);

  const groupInputRef = useRef<HTMLInputElement>(null);
  const skillInputRef = useRef<HTMLInputElement>(null);

  // ─── Load initial data ─────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [s, ss, p, c] = await Promise.all([
          fetchReferential('sectors'),
          fetchReferential('subsectors'),
          fetchReferential('positions'),
          fetchReferential('certifications'),
        ]);
        setSectors(s);
        setSubsectors(ss);
        setPositions(p);
        setCertifications(c);
        if (s.length > 0) {
          setExpandedSectors(new Set([s[0].id]));
          const firstSectorSubs = ss.filter((sub) => sub.sectorId === s[0].id).map((sub) => sub.id);
          setExpandedSubsectors(new Set(firstSectorSubs));
        }
      } catch (err) {
        logger.error('load_referentials', 'Referentials', { error: String(err) });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ─── Recherche de compétences (débouncée, DB) ──────────────────────────

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setSkillMatchPositionIds(null);
      return;
    }
    const timer = setTimeout(() => {
      searchSkillPositions(q)
        .then(setSkillMatchPositionIds)
        .catch(() => setSkillMatchPositionIds(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ─── Auto-expand on search ──────────────────────────────────────────────

  useEffect(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return;

    const matchingPositions = positions.filter(
      (p) => p.nameFr.toLowerCase().includes(q) || skillMatchPositionIds?.has(p.id)
    );
    const sectorIds = new Set(matchingPositions.map((p) => p.sectorId).filter(Boolean) as string[]);
    const subsectorIds = new Set(matchingPositions.map((p) => p.subsectorId).filter(Boolean) as string[]);

    setExpandedSectors((prev) => {
      const next = new Set(prev);
      sectorIds.forEach((id) => next.add(id));
      return next;
    });
    setExpandedSubsectors((prev) => {
      const next = new Set(prev);
      subsectorIds.forEach((id) => next.add(id));
      return next;
    });
  }, [searchQuery, positions, skillMatchPositionIds]);

  // ─── Load position detail ──────────────────────────────────────────────

  useEffect(() => {
    if (!selectedPositionId) {
      setPositionDetail(null);
      setPositionCerts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setAddingCert(false);
      try {
        fetchPositionCertifications(selectedPositionId)
          .then((certs) => { if (!cancelled) setPositionCerts(certs); })
          .catch(() => { if (!cancelled) setPositionCerts([]); });

        const positionGroups = await fetchGroupsByPosition(selectedPositionId);

        const skillsByGroup: Record<string, RefItem[]> = {};
        await Promise.all(
          positionGroups.map(async (g) => {
            const skills = await fetchSkillsByGroup(g.id);
            if (!cancelled) skillsByGroup[g.id] = skills;
          })
        );

        if (!cancelled) {
          setPositionDetail({ groups: positionGroups, skillsByGroup });
          setExpandedGroups(new Set(positionGroups.map((g) => g.id)));
        }
      } catch (err) {
        logger.error('load_position_detail', 'Referentials', { error: String(err) });
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPositionId]);

  // ─── Derived data ──────────────────────────────────────────────────────

  const selectedPosition = useMemo(
    () => positions.find((p) => p.id === selectedPositionId) || null,
    [positions, selectedPositionId]
  );

  /**
   * Un objet obsolète reste dans l'arbre tant que l'interrupteur est actif,
   * sinon il devient introuvable donc irrécupérable.
   */
  const isVisible = useCallback(
    (item: RefItem) => showObsolete || item.active,
    [showObsolete],
  );

  const visibleSectors = useMemo(() => sectors.filter(isVisible), [sectors, isVisible]);

  const getSubsectorsForSector = useMemo(() => {
    return (sectorId: string) => subsectors.filter((ss) => ss.sectorId === sectorId && isVisible(ss));
  }, [subsectors, isVisible]);

  /** Fratrie complète d'un sous-secteur, obsolètes compris : base du réordonnancement. */
  const orderedSubsectors = useCallback(
    (sectorId: string) =>
      subsectors.filter((ss) => ss.sectorId === sectorId).sort((a, b) => a.sortOrder - b.sortOrder),
    [subsectors],
  );

  /** Sous-secteurs d'un secteur groupés par catégorie (pôle), ordre d'apparition. */
  const getGroupedSubsectors = useMemo(() => {
    return (sectorId: string) => {
      const subs = subsectors
        .filter((ss) => ss.sectorId === sectorId && isVisible(ss))
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const groups: { category: string | null; subs: RefItem[] }[] = [];
      for (const ss of subs) {
        const cat = ss.category || null;
        const last = groups[groups.length - 1];
        if (last && last.category === cat) last.subs.push(ss);
        else groups.push({ category: cat, subs: [ss] });
      }
      return groups;
    };
  }, [subsectors, isVisible]);

  // Un poste matche si son nom correspond OU s'il contient une compétence qui matche
  const positionMatchesQuery = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return (p: RefItem) =>
      isVisible(p) &&
      (!q || p.nameFr.toLowerCase().includes(q) || (skillMatchPositionIds?.has(p.id) ?? false));
  }, [searchQuery, skillMatchPositionIds, isVisible]);

  const filteredPositionsBySector = useMemo(() => {
    return (sectorId: string) =>
      positions.filter((p) => p.sectorId === sectorId && positionMatchesQuery(p));
  }, [positions, positionMatchesQuery]);

  const filteredPositionsBySubsector = useMemo(() => {
    return (subsectorId: string) =>
      positions
        .filter((p) => p.subsectorId === subsectorId && positionMatchesQuery(p))
        .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [positions, positionMatchesQuery]);

  const unclassifiedPositions = useMemo(() => {
    return (sectorId: string) =>
      positions
        .filter((p) => p.sectorId === sectorId && !p.subsectorId && positionMatchesQuery(p))
        .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [positions, positionMatchesQuery]);

  const getSectorName = useCallback(
    (id: string) => sectors.find((s) => s.id === id)?.nameFr || '',
    [sectors]
  );

  // ─── Handlers ──────────────────────────────────────────────────────────

  const cancelAddPosition = useCallback(() => {
    setAddPosTarget(null);
    setNewPositionName('');
  }, []);

  const isAddTarget = useCallback(
    (sectorId: string, subsectorId: string | undefined, afterId: string) =>
      addPosTarget?.sectorId === sectorId &&
      addPosTarget?.subsectorId === subsectorId &&
      addPosTarget?.afterId === afterId,
    [addPosTarget]
  );

  /** Un ajout est en cours à la racine du secteur (poste hors sous-secteur). */
  const isAddingAtSectorRoot = useCallback(
    (sectorId: string) => addPosTarget?.sectorId === sectorId && !addPosTarget.subsectorId,
    [addPosTarget]
  );

  /** Fratrie d'un poste : même secteur et même sous-secteur, triée. */
  const positionSiblings = useCallback(
    (sectorId: string | undefined, subsectorId: string | undefined) =>
      positions
        .filter((p) => p.sectorId === sectorId && (subsectorId ? p.subsectorId === subsectorId : !p.subsectorId))
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [positions]
  );

  const handleAddPosition = async () => {
    if (!addPosTarget) return;
    const name = newPositionName.trim();
    if (!name) return;

    const { sectorId, subsectorId, afterId } = addPosTarget;
    const siblings = positionSiblings(sectorId, subsectorId);

    // Un sort_order intercalaire évite de renuméroter toute la fratrie
    const afterIdx = afterId === 'top' ? -1 : siblings.findIndex((p) => p.id === afterId);
    const sortOrder =
      afterIdx === -1
        ? (siblings[0]?.sortOrder ?? 0) - 1
        : afterIdx < siblings.length - 1
          ? (siblings[afterIdx].sortOrder + siblings[afterIdx + 1].sortOrder) / 2
          : siblings[afterIdx].sortOrder + 1;

    try {
      const newItem = await quickCreatePosition(name, sectorId, subsectorId, sortOrder);
      setPositions((prev) => [...prev, newItem].sort((a, b) => a.sortOrder - b.sortOrder));
      cancelAddPosition();
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const openCreateSector = () => {
    setCreateName('');
    setCreateCode('');
    setCreateCodeDirty(false);
    setCreateCategory('');
    setCreateSubName('');
    if (createLogoPreview) URL.revokeObjectURL(createLogoPreview);
    if (createPhotoPreview) URL.revokeObjectURL(createPhotoPreview);
    setCreateLogoFile(null);
    setCreateLogoPreview(null);
    setCreatePhotoFile(null);
    setCreatePhotoPreview(null);
    setCreateModal({ type: 'sector' });
  };

  const pickVisual = (kind: 'logo' | 'photo', file: File | undefined) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (kind === 'logo') {
      if (createLogoPreview) URL.revokeObjectURL(createLogoPreview);
      setCreateLogoFile(file);
      setCreateLogoPreview(URL.createObjectURL(file));
    } else {
      if (createPhotoPreview) URL.revokeObjectURL(createPhotoPreview);
      setCreatePhotoFile(file);
      setCreatePhotoPreview(URL.createObjectURL(file));
    }
  };

  const clearVisual = (kind: 'logo' | 'photo') => {
    if (kind === 'logo') {
      if (createLogoPreview) URL.revokeObjectURL(createLogoPreview);
      setCreateLogoFile(null);
      setCreateLogoPreview(null);
    } else {
      if (createPhotoPreview) URL.revokeObjectURL(createPhotoPreview);
      setCreatePhotoFile(null);
      setCreatePhotoPreview(null);
    }
  };

  const openCreateSubsector = (sectorId: string, category?: string) => {
    setCreateName('');
    setCreateCode('');
    setCreateCodeDirty(false);
    setCreateCategory(category || '');
    setCreateSubName('');
    setCreateModal({ type: 'subsector', sectorId });
  };

  const handleCreateNameChange = (value: string) => {
    setCreateName(value);
    if (!createCodeDirty && createModal?.type === 'sector') {
      setCreateCode(slugify(value).slice(0, 50));
    }
  };

  const handleCreateSubmit = async () => {
    if (!createModal) return;
    const name = createName.trim();
    if (!name) return;
    setCreating(true);
    try {
      if (createModal.type === 'sector') {
        const codeHint = createCode.trim() || undefined;
        const sectorDraft = await quickCreateSector(name, codeHint, sectors.length);
        let logoUrl: string | null = null;
        let imageUrl: string | null = null;
        try {
          if (createLogoFile) logoUrl = await uploadSectorAsset(sectorDraft.code, 'logo', createLogoFile);
          if (createPhotoFile) imageUrl = await uploadSectorAsset(sectorDraft.code, 'photo', createPhotoFile);
        } catch (visErr) {
          alert(
            `Secteur créé, mais le visuel a échoué : ${visErr instanceof Error ? visErr.message : String(visErr)}`
          );
        }
        const finalLogo = logoUrl || imageUrl;
        const finalPhoto = imageUrl || logoUrl;
        let sector = sectorDraft;
        if (finalLogo || finalPhoto) {
          await updateReferentialItem('sectors', sectorDraft.id, { logoUrl: finalLogo, imageUrl: finalPhoto });
          sector = { ...sectorDraft, logoUrl: finalLogo, imageUrl: finalPhoto };
        }
        setSectors((prev) => [...prev, sector].sort((a, b) => a.sortOrder - b.sortOrder));
        setExpandedSectors((prev) => new Set(prev).add(sector.id));
        setCreateModal(null);

        const firstSubName = createSubName.trim();
        if (firstSubName) {
          try {
            const sub = await quickCreateSubsector(
              firstSubName,
              sector.id,
              sector.code,
              createCategory.trim() || undefined,
              0,
            );
            setSubsectors((prev) => [...prev, sub]);
            setExpandedSubsectors((prev) => new Set(prev).add(sub.id));
          } catch (subErr) {
            alert(
              `Secteur créé, mais le sous-secteur a échoué : ${subErr instanceof Error ? subErr.message : String(subErr)}`
            );
          }
        }
      } else {
        const sector = sectors.find((s) => s.id === createModal.sectorId);
        const siblings = subsectors.filter((ss) => ss.sectorId === createModal.sectorId);
        const sub = await quickCreateSubsector(
          name,
          createModal.sectorId,
          sector?.code,
          createCategory.trim() || undefined,
          siblings.length,
        );
        setSubsectors((prev) => [...prev, sub]);
        setExpandedSectors((prev) => new Set(prev).add(createModal.sectorId));
        setExpandedSubsectors((prev) => new Set(prev).add(sub.id));
        setCreateModal(null);
      }
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleMovePosition = async (pos: RefItem, dir: -1 | 1) => {
    const result = moveInList(positionSiblings(pos.sectorId, pos.subsectorId), pos.id, dir);
    if (!result) return;

    const newOrders = new Map(result.list.map((p) => [p.id, p.sortOrder]));
    setPositions((prev) =>
      prev.map((p) => (newOrders.has(p.id) ? { ...p, sortOrder: newOrders.get(p.id)! } : p))
    );

    try {
      await reorderReferentialItems('positions', result.changes);
    } catch (err) {
      alert(`Erreur: ${err}`);
      setPositions(await fetchReferential('positions'));
    }
  };

  const handleMoveGroup = async (groupId: string, dir: -1 | 1) => {
    if (!positionDetail) return;
    const result = moveInList(positionDetail.groups, groupId, dir);
    if (!result) return;

    setPositionDetail((prev) => (prev ? { ...prev, groups: result.list } : prev));

    try {
      await reorderReferentialItems('skill_groups', result.changes);
    } catch (err) {
      alert(`Erreur: ${err}`);
      await refreshPositionDetail();
    }
  };

  const handleMoveSkill = async (groupId: string, skillId: string, dir: -1 | 1) => {
    if (!positionDetail) return;
    const result = moveInList(positionDetail.skillsByGroup[groupId] || [], skillId, dir);
    if (!result) return;

    setPositionDetail((prev) =>
      prev ? { ...prev, skillsByGroup: { ...prev.skillsByGroup, [groupId]: result.list } } : prev
    );

    try {
      await reorderReferentialItems('skills', result.changes);
    } catch (err) {
      alert(`Erreur: ${err}`);
      await refreshPositionDetail();
    }
  };

  const handleAddGroup = async () => {
    if (!selectedPositionId || !selectedPosition) return;
    const name = newGroupName.trim();
    if (!name) return;
    try {
      const newItem = await quickCreateGroup(
        name,
        selectedPositionId,
        selectedPosition.code,
        positionDetail?.groups.length ?? 0
      );
      setPositionDetail((prev) => {
        if (!prev) return prev;
        return { ...prev, groups: [...prev.groups, newItem], skillsByGroup: { ...prev.skillsByGroup, [newItem.id]: [] } };
      });
      setExpandedGroups((prev) => new Set([...prev, newItem.id]));
      setNewGroupName('');
      setAddingGroup(false);
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const handleAddSkill = async (groupId: string) => {
    if (!selectedPositionId || !selectedPosition) return;
    const name = newSkillName.trim();
    if (!name) return;
    try {
      const newItem = await quickCreateSkill(
        name,
        groupId,
        selectedPositionId,
        selectedPosition.sectorId || '',
        selectedPosition.code,
        positionDetail?.skillsByGroup[groupId]?.length ?? 0
      );
      setPositionDetail((prev) => {
        if (!prev) return prev;
        const updated = { ...prev.skillsByGroup };
        updated[groupId] = [...(updated[groupId] || []), newItem];
        return { ...prev, skillsByGroup: updated };
      });
      setNewSkillName('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  // ─── Obsolescence ──────────────────────────────────────────────────────

  /** Ouvre le récapitulatif d'usage : on ne rend rien obsolète à l'aveugle. */
  const openDeprecate = (kind: NodeKind, item: RefItem) => setDeprecate({ kind, item });

  const handleRestore = async (kind: NodeKind, item: RefItem) => {
    try {
      await restoreReferentialItem(NODE_TABLE[kind], item.id);
      await reloadTree();
      if (kind === 'group' || kind === 'skill') await refreshPositionDetail();
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // ─── Édition unifiée ───────────────────────────────────────────────────

  const openEdit = (kind: NodeKind, item: RefItem) => {
    const parentPositionId =
      kind === 'group' ? item.positionId || '' : kind === 'skill' ? item.positionId || '' : '';
    const parentSectorId =
      kind === 'subsector' || kind === 'position'
        ? item.sectorId || ''
        : parentPositionId
          ? positions.find((p) => p.id === parentPositionId)?.sectorId || ''
          : '';

    setEdit({
      kind,
      item,
      nameFr: item.nameFr,
      code: item.code,
      category: item.category || '',
      sectorId: parentSectorId,
      subsectorId: kind === 'position' ? item.subsectorId || '' : '',
      positionId: parentPositionId,
      groupId: kind === 'skill' ? item.skillGroupId || '' : '',
      saving: false,
    });
    setEditGroupOptions(kind === 'skill' ? (positionDetail?.groups ?? []) : []);
  };

  /** Le parent choisi diffère-t-il du parent actuel ? */
  const editHasMoved = (e: EditState): boolean => {
    switch (e.kind) {
      case 'subsector':
        return !!e.sectorId && e.sectorId !== e.item.sectorId;
      case 'position':
        return (
          (!!e.sectorId && e.sectorId !== e.item.sectorId) ||
          (e.subsectorId || '') !== (e.item.subsectorId || '')
        );
      case 'group':
        return !!e.positionId && e.positionId !== e.item.positionId;
      case 'skill':
        return !!e.groupId && e.groupId !== e.item.skillGroupId;
      default:
        return false;
    }
  };

  const handleEditSave = async () => {
    if (!edit) return;
    const name = edit.nameFr.trim();
    const code = edit.code.trim();
    if (!name || !code) return;

    setEdit({ ...edit, saving: true });
    const table = NODE_TABLE[edit.kind];
    try {
      const patch: Partial<Omit<RefItem, 'id'>> = {};
      if (name !== edit.item.nameFr) patch.nameFr = name;
      if (code !== edit.item.code) patch.code = code;
      if (edit.kind === 'subsector' && edit.category !== (edit.item.category || '')) {
        patch.category = edit.category;
      }
      if (Object.keys(patch).length > 0) {
        await updateReferentialItem(table, edit.item.id, patch);
      }

      // Le reparentage passe par la RPC : elle propage les rattachements
      // aux descendants (une compétence déplacée hérite du poste et du secteur).
      if (editHasMoved(edit)) {
        if (edit.kind === 'position') {
          await moveReferentialNode(table, edit.item.id, edit.sectorId, edit.subsectorId || null);
        } else {
          const parentId =
            edit.kind === 'subsector' ? edit.sectorId : edit.kind === 'group' ? edit.positionId : edit.groupId;
          await moveReferentialNode(table, edit.item.id, parentId);
        }
      }

      await reloadTree();
      if (edit.kind === 'group' || edit.kind === 'skill') await refreshPositionDetail();
      setEdit(null);
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
      setEdit((prev) => (prev ? { ...prev, saving: false } : prev));
    }
  };

  // ─── Réordonnancement secteurs / sous-secteurs ─────────────────────────

  const handleMoveSector = async (sectorId: string, dir: -1 | 1) => {
    const result = moveInList([...sectors].sort((a, b) => a.sortOrder - b.sortOrder), sectorId, dir);
    if (!result) return;
    setSectors(result.list);
    try {
      await reorderReferentialItems('sectors', result.changes);
    } catch (err) {
      alert(`Erreur: ${err}`);
      setSectors(await fetchReferential('sectors'));
    }
  };

  const handleMoveSubsector = async (sub: RefItem, dir: -1 | 1) => {
    const siblings = subsectors
      .filter((ss) => ss.sectorId === sub.sectorId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const result = moveInList(siblings, sub.id, dir);
    if (!result) return;

    const newOrders = new Map(result.list.map((s) => [s.id, s.sortOrder]));
    setSubsectors((prev) =>
      prev.map((s) => (newOrders.has(s.id) ? { ...s, sortOrder: newOrders.get(s.id)! } : s)),
    );
    try {
      await reorderReferentialItems('subsectors', result.changes);
    } catch (err) {
      alert(`Erreur: ${err}`);
      setSubsectors(await fetchReferential('subsectors'));
    }
  };

  // ─── Certifications du poste ───────────────────────────────────────────

  const handleLinkCertification = async (certificationId: string) => {
    if (!selectedPositionId || !certificationId) return;
    try {
      await linkPositionCertification(selectedPositionId, certificationId, false);
      setPositionCerts(await fetchPositionCertifications(selectedPositionId));
      setAddingCert(false);
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleToggleCertificationRequired = async (cert: PositionCertification) => {
    if (!selectedPositionId) return;
    try {
      await linkPositionCertification(selectedPositionId, cert.certificationId, !cert.required);
      setPositionCerts((prev) =>
        prev.map((c) => (c.certificationId === cert.certificationId ? { ...c, required: !c.required } : c)),
      );
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleUnlinkCertification = async (certificationId: string) => {
    if (!selectedPositionId) return;
    try {
      await unlinkPositionCertification(selectedPositionId, certificationId);
      setPositionCerts((prev) => prev.filter((c) => c.certificationId !== certificationId));
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // ─── Translation handler ────────────────────────────────────────────────

  /** Recharge les trois niveaux de l'arbre après un déplacement ou une obsolescence. */
  const reloadTree = useCallback(async () => {
    const [s, ss, p] = await Promise.all([
      fetchReferential('sectors'),
      fetchReferential('subsectors'),
      fetchReferential('positions'),
    ]);
    setSectors(s);
    setSubsectors(ss);
    setPositions(p);
  }, []);

  const refreshPositionDetail = useCallback(async () => {
    if (!selectedPositionId) return;
    try {
      const positionGroups = await fetchGroupsByPosition(selectedPositionId);
      const skillsByGroup: Record<string, RefItem[]> = {};
      await Promise.all(
        positionGroups.map(async (g) => {
          skillsByGroup[g.id] = await fetchSkillsByGroup(g.id);
        })
      );
      setPositionDetail({ groups: positionGroups, skillsByGroup });
    } catch { /* silent */ }
  }, [selectedPositionId]);

  const handleTranslatePosition = async () => {
    if (!selectedPositionId || !positionDetail) return;
    setTranslating(true);
    setTranslateProgress('Préparation…');
    try {
      const groupIds = positionDetail.groups.map((g) => g.id);
      const skillIds = Object.values(positionDetail.skillsByGroup).flat().map((s) => s.id);
      const totalItems = 1 + groupIds.length + skillIds.length;

      // Complète uniquement les langues manquantes (lots courts, reprise automatique).
      const scopes: { table: 'positions' | 'skill_groups' | 'skills'; ids: string[] }[] = [
        { table: 'positions', ids: [selectedPositionId] },
        { table: 'skill_groups', ids: groupIds },
        { table: 'skills', ids: skillIds },
      ];
      let translated = 0;
      let skipped = 0;
      let failed = 0;
      const errors: string[] = [];
      for (const scope of scopes) {
        if (scope.ids.length === 0) continue;
        const res = await runMissingTranslations({
          tables: [scope.table],
          ids: scope.ids,
          onProgress: (p) => setTranslateProgress(`${p.message} (${translated + p.translated} traduits)`),
          onError: (msg) => errors.push(msg),
        });
        translated += res.translated;
        skipped += res.skipped;
        failed += res.failed;
      }

      // Refresh data to update indicators (le drapeau « traduit » du poste
      // vit dans la liste de l'arbre, celui des groupes et compétences dans le détail)
      await Promise.all([refreshPositionDetail(), reloadTree()]);

      const nbLangs = TRANSLATION_LANGS.length;
      if (errors.length > 0) {
        const detail = errors.slice(0, 5).join('\n');
        alert(
          `Traduction : ${translated} élément(s) complété(s), ${failed} en échec sur ${totalItems}.\n\n`
          + `Erreurs :\n${detail}${errors.length > 5 ? `\n… et ${errors.length - 5} autres` : ''}`,
        );
      } else if (translated > 0) {
        alert(`Traduction terminée : ${translated}/${totalItems} élément(s) complété(s) en ${nbLangs} langues${skipped > 0 ? ` (${skipped} déjà complets)` : ''}.`);
      } else {
        alert(`Déjà complet : les ${totalItems} éléments de ce poste ont leurs ${nbLangs} langues.`);
      }
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : err}`);
    } finally {
      setTranslating(false);
      setTranslateProgress('');
    }
  };

  const openTranslationPanel = (item: RefItem, table: RefTable) => {
    setTranslationItem({ item, table });
    setTranslationEdits(translationEditsFromItem(item));
  };

  const handleSaveTranslation = async (lang: string) => {
    if (!translationItem) return;
    setTranslationSaving(true);
    try {
      await updateTranslation(translationItem.table, translationItem.item.id, lang, translationEdits[lang] || '', true);
      // Update local state
      const updated = { ...translationItem.item, [`name${lang.charAt(0).toUpperCase() + lang.slice(1)}`]: translationEdits[lang] };
      updated.translationLocked = { ...(updated.translationLocked || {}), [lang]: true };
      setTranslationItem({ ...translationItem, item: updated });
      await refreshPositionDetail();
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setTranslationSaving(false);
    }
  };

  const handleToggleLock = async (lang: string) => {
    if (!translationItem) return;
    const currentLocked = translationItem.item.translationLocked?.[lang] || false;
    try {
      const newLocks = await toggleTranslationLock(translationItem.table, translationItem.item.id, lang, !currentLocked);
      const updated = { ...translationItem.item, translationLocked: Object.keys(newLocks).length > 0 ? newLocks : null };
      setTranslationItem({ ...translationItem, item: updated });
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  // ─── Import handlers ───────────────────────────────────────────────────

  const handleImported = useCallback(async () => {
    setPositions(await fetchReferential('positions'));
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────

  /** Liste de postes d'une fratrie, avec points d'insertion et flèches. */
  const renderPositionList = (
    sectorId: string,
    subsectorId: string | undefined,
    list: RefItem[],
    indent: string,
  ) => {
    const insertPoint = (afterId: string) => (
      <InsertPoint
        active={isAddTarget(sectorId, subsectorId, afterId)}
        value={newPositionName}
        onChange={setNewPositionName}
        onInsert={handleAddPosition}
        onCancel={cancelAddPosition}
        onActivate={() => { setAddPosTarget({ sectorId, subsectorId, afterId }); setNewPositionName(''); }}
        indent={indent}
      />
    );

    return (
      <>
        {insertPoint('top')}
        {list.map((pos, idx) => (
          <React.Fragment key={pos.id}>
            <div
              {...clickableRow(() => setSelectedPositionId(pos.id))}
              className={`w-full px-4 ${indent} py-2 text-left text-sm transition-colors flex items-center gap-2 cursor-pointer group/item ${
                selectedPositionId === pos.id
                  ? 'bg-[#00244F]/10 text-[#00244F] font-medium border-r-2 border-[#00244F]'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${pos.active ? 'bg-green-400' : 'bg-amber-400'}`} />
              <span className={`truncate flex-1 ${pos.active ? '' : 'line-through text-gray-400'}`}>{pos.nameFr}</span>
              {/* Le classement porte sur la liste complète : masqué pendant une recherche */}
              {!searchQuery && (
                <MoveButtons
                  onUp={() => handleMovePosition(pos, -1)}
                  onDown={() => handleMovePosition(pos, 1)}
                  isFirst={idx === 0}
                  isLast={idx === list.length - 1}
                />
              )}
              <NodeActions
                active={pos.active}
                onEdit={() => openEdit('position', pos)}
                onDeprecate={() => openDeprecate('position', pos)}
                onRestore={() => handleRestore('position', pos)}
              />
            </div>
            {insertPoint(pos.id)}
          </React.Fragment>
        ))}
      </>
    );
  };

  /** Ouvre la saisie en fin de fratrie et déplie ce qu'il faut. */
  const startAddPosition = (sectorId: string, subsectorId?: string) => {
    const siblings = positionSiblings(sectorId, subsectorId);
    setExpandedSectors((prev) => new Set(prev).add(sectorId));
    if (subsectorId) setExpandedSubsectors((prev) => new Set(prev).add(subsectorId));
    setNewPositionName('');
    setAddPosTarget({
      sectorId,
      subsectorId,
      afterId: siblings[siblings.length - 1]?.id ?? 'top',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Référentiels</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {sectors.length} secteurs, {positions.length} postes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showObsolete}
              onChange={(e) => setShowObsolete(e.target.checked)}
              className="rounded border-gray-300 text-[#00244F] focus:ring-[#00244F]/20"
            />
            Afficher les obsolètes
          </label>
          <button
            onClick={openCreateSector}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors"
          >
            Nouveau secteur
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors flex items-center gap-2"
          >
            <span>📄</span> Importer un fichier
          </button>
          <button
            onClick={() => setShowMissingTranslations(true)}
            className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors flex items-center gap-2"
            title="Traduire tout ce qui manque, toutes tables et toutes langues"
          >
            <span>🌐</span> Compléter les traductions
          </button>
        </div>
      </div>

      {/* Split panel */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel */}
        <div className="w-[320px] border-r border-gray-200 bg-gray-50/50 flex flex-col flex-shrink-0">
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un poste ou une compétence..."
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 placeholder-gray-400"
            />
          </div>

          {/* Sectors accordion */}
          <div className="flex-1 overflow-y-auto">
            {visibleSectors.map((sector, sectorIdx) => {
              const isExpanded = expandedSectors.has(sector.id);
              const sectorPositions = filteredPositionsBySector(sector.id);

              return (
                <div key={sector.id} className="border-b border-gray-100 last:border-b-0">
                  <div
                    {...clickableRow(() => {
                      setExpandedSectors((prev) => {
                        const next = new Set(prev);
                        if (next.has(sector.id)) {
                          next.delete(sector.id);
                        } else {
                          next.add(sector.id);
                          const subs = subsectors.filter((ss) => ss.sectorId === sector.id);
                          setExpandedSubsectors((p) => {
                            const n = new Set(p);
                            subs.forEach((s) => n.add(s.id));
                            return n;
                          });
                        }
                        return next;
                      });
                    })}
                    className="w-full px-4 py-3 flex items-center gap-2 hover:bg-gray-100 transition-colors text-left cursor-pointer group/item"
                  >
                    <span className={`text-[10px] text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                    {sector.logoUrl ? (
                      <img src={sector.logoUrl} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0 bg-white" />
                    ) : null}
                    <span className={`text-sm font-semibold flex-1 truncate ${sector.active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                      {sector.nameFr}
                    </span>
                    {!sector.active && <ObsoleteBadge />}
                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-gray-200 text-gray-500">
                      {sectorPositions.length}
                    </span>
                    {!searchQuery && (
                      <MoveButtons
                        onUp={() => handleMoveSector(sector.id, -1)}
                        onDown={() => handleMoveSector(sector.id, 1)}
                        isFirst={sectorIdx === 0}
                        isLast={sectorIdx === visibleSectors.length - 1}
                      />
                    )}
                    <NodeActions
                      active={sector.active}
                      onEdit={() => openEdit('sector', sector)}
                      onDeprecate={() => openDeprecate('sector', sector)}
                      onRestore={() => handleRestore('sector', sector)}
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); startAddPosition(sector.id); }}
                      title="Ajouter un poste (hors sous-secteur)"
                      className="w-5 h-5 flex items-center justify-center rounded text-base leading-none text-gray-400 hover:text-white hover:bg-[#00244F] transition-colors"
                    >
                      +
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="pb-2">
                      {/* Positions grouped by category > subsector (foldable) */}
                      {getGroupedSubsectors(sector.id).map((catGroup, cgIdx) => {
                        const visibleSubs = catGroup.subs.filter(
                          (ss) => !(filteredPositionsBySubsector(ss.id).length === 0 && searchQuery)
                        );
                        if (visibleSubs.length === 0) return null;
                        return (
                          <div key={catGroup.category || `cat_${cgIdx}`}>
                            {catGroup.category && (
                              <div className="px-4 pl-5 pt-2 pb-0.5 flex items-center gap-1">
                                <span className="text-[10px] uppercase tracking-widest text-[#00244F]/60 font-bold flex-1">
                                  {catGroup.category}
                                </span>
                                {!searchQuery && (
                                  <button
                                    type="button"
                                    onClick={() => openCreateSubsector(sector.id, catGroup.category || undefined)}
                                    title={`Ajouter un sous-secteur dans ${catGroup.category}`}
                                    className="w-5 h-5 flex items-center justify-center rounded text-sm leading-none text-gray-400 hover:text-white hover:bg-[#00244F] transition-colors"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            )}
                            {visibleSubs.map((ss) => {
                        const ssPositions = filteredPositionsBySubsector(ss.id);
                        const isSubExpanded = expandedSubsectors.has(ss.id);
                        const ssSiblings = orderedSubsectors(sector.id);
                        const ssIdx = ssSiblings.findIndex((s) => s.id === ss.id);
                        return (
                          <div key={ss.id}>
                            <div
                              {...clickableRow(() => {
                                setExpandedSubsectors((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(ss.id)) next.delete(ss.id);
                                  else next.add(ss.id);
                                  return next;
                                });
                              })}
                              className="w-full px-4 pl-6 py-1.5 flex items-center gap-1.5 hover:bg-gray-100 transition-colors mt-1 text-left cursor-pointer group/item"
                            >
                              <span className={`text-[8px] text-gray-400 transition-transform duration-200 ${isSubExpanded ? 'rotate-90' : ''}`}>
                                ▶
                              </span>
                              <span className={`text-[11px] uppercase tracking-wider font-semibold flex-1 truncate ${ss.active ? 'text-gray-400' : 'text-gray-300 line-through'}`}>
                                {ss.nameFr}
                              </span>
                              {!ss.active && <ObsoleteBadge />}
                              <span className="text-[10px] text-gray-300">{ssPositions.length}</span>
                              {!searchQuery && (
                                <MoveButtons
                                  onUp={() => handleMoveSubsector(ss, -1)}
                                  onDown={() => handleMoveSubsector(ss, 1)}
                                  isFirst={ssIdx === 0}
                                  isLast={ssIdx === ssSiblings.length - 1}
                                />
                              )}
                              <NodeActions
                                active={ss.active}
                                onEdit={() => openEdit('subsector', ss)}
                                onDeprecate={() => openDeprecate('subsector', ss)}
                                onRestore={() => handleRestore('subsector', ss)}
                              />
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); startAddPosition(sector.id, ss.id); }}
                                title={`Ajouter un poste dans ${ss.nameFr}`}
                                className="w-5 h-5 flex items-center justify-center rounded text-sm leading-none text-gray-400 hover:text-white hover:bg-[#00244F] transition-colors"
                              >
                                +
                              </button>
                            </div>
                            {isSubExpanded && renderPositionList(sector.id, ss.id, ssPositions, 'pl-10')}
                          </div>
                        );
                            })}
                          </div>
                        );
                      })}

                      {/* Positions without subsector */}
                      {(() => {
                        const hasSubs = getSubsectorsForSector(sector.id).length > 0;
                        const ucPositions = unclassifiedPositions(sector.id);
                        if (ucPositions.length === 0 && !isAddingAtSectorRoot(sector.id)) {
                          return hasSubs ? null : (
                            <button
                              onClick={() => startAddPosition(sector.id)}
                              className="w-full px-4 pl-8 py-1.5 text-left text-xs text-gray-400 hover:text-[#00244F] hover:bg-gray-100 transition-colors"
                            >
                              + Ajouter un poste
                            </button>
                          );
                        }
                        return (
                          <div>
                            {hasSubs && (
                              <div className="px-4 pl-6 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 font-semibold mt-1">
                                Autres
                              </div>
                            )}
                            {renderPositionList(sector.id, undefined, ucPositions, hasSubs ? 'pl-10' : 'pl-8')}
                          </div>
                        );
                      })()}

                      {!searchQuery && (
                        <button
                          type="button"
                          onClick={() => openCreateSubsector(sector.id)}
                          className="w-full px-4 pl-8 py-1.5 text-left text-xs text-gray-400 hover:text-[#00244F] hover:bg-gray-100 transition-colors"
                        >
                          + Ajouter un sous-secteur
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {!searchQuery && (
              <button
                type="button"
                onClick={openCreateSector}
                className="w-full px-4 py-3 text-left text-sm text-gray-400 hover:text-[#00244F] hover:bg-gray-100 transition-colors"
              >
                + Nouveau secteur
              </button>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 overflow-y-auto bg-white">
          {!selectedPositionId ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-5xl mb-4 opacity-30">⚙️</div>
                <p className="text-gray-400 text-sm">Sélectionnez un poste pour voir ses compétences</p>
              </div>
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-6 h-6 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
            </div>
          ) : selectedPosition && positionDetail ? (
            <div className="p-6">
              {/* Position header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span
                      className="cursor-pointer hover:text-[#00244F]"
                      onClick={() => openTranslationPanel(selectedPosition, 'positions')}
                      title="Voir les traductions"
                    >
                      {selectedPosition.nameFr}
                    </span>
                    {!selectedPosition.translated ? (
                      <span className="px-2 py-0.5 text-[10px] bg-orange-100 text-orange-600 font-medium rounded-md">
                        Non traduit
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] bg-green-100 text-green-600 font-medium rounded-md">
                        Traduit ✓
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="px-2 py-0.5 text-[10px] bg-blue-50 text-blue-600 font-medium rounded-md">
                      {getSectorName(selectedPosition.sectorId || '')}
                    </span>
                    {selectedPosition.category && (
                      <span className="px-2 py-0.5 text-[10px] bg-indigo-50 text-indigo-600 font-medium rounded-md">
                        {selectedPosition.category}
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-[10px] bg-gray-100 text-gray-500 font-mono rounded">
                      {selectedPosition.code}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                      selectedPosition.active ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                    }`}
                  >
                    {selectedPosition.active ? 'Actif' : 'Obsolète'}
                  </span>
                  <button
                    onClick={handleTranslatePosition}
                    disabled={translating}
                    title={translating ? translateProgress : `Compléter les langues manquantes (${TRANSLATION_LANGS.length} langues)`}
                    className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-40 flex items-center gap-1"
                  >
                    {translating ? (
                      <><span className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" /> Traduction...</>
                    ) : (
                      'Traduire'
                    )}
                  </button>
                  <button
                    onClick={() => openEdit('position', selectedPosition)}
                    className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    Modifier
                  </button>
                  {selectedPosition.active ? (
                    <button
                      onClick={() => openDeprecate('position', selectedPosition)}
                      className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                    >
                      Rendre obsolète
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRestore('position', selectedPosition)}
                      className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                    >
                      Restaurer
                    </button>
                  )}
                </div>
              </div>

              {translating && translateProgress && (
                <div className="mb-4 px-3 py-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg">
                  {translateProgress}
                </div>
              )}

              {/* Groups & Skills */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Groupes de compétences ({positionDetail.groups.length})
                  </h3>
                </div>

                {positionDetail.groups.filter(isVisible).map((group, groupIdx, visibleGroups) => {
                  const isGroupExpanded = expandedGroups.has(group.id);
                  const skills = (positionDetail.skillsByGroup[group.id] || []).filter(isVisible);

                  return (
                    <div key={group.id} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Group header */}
                      <div
                        className="px-4 py-3 bg-gray-50 flex items-center gap-2 cursor-pointer hover:bg-gray-100 transition-colors group/item"
                        onClick={() => {
                          setExpandedGroups((prev) => {
                            const next = new Set(prev);
                            if (next.has(group.id)) next.delete(group.id);
                            else next.add(group.id);
                            return next;
                          });
                        }}
                      >
                        <span className={`text-[10px] text-gray-400 transition-transform duration-200 ${isGroupExpanded ? 'rotate-90' : ''}`}>
                          ▶
                        </span>
                        <span
                          className={`text-sm font-medium flex-1 cursor-pointer hover:text-[#00244F] ${group.active ? 'text-gray-900' : 'text-gray-400 line-through'}`}
                          onClick={(e) => { e.stopPropagation(); openTranslationPanel(group, 'skill_groups'); }}
                        >{group.nameFr}</span>
                        {!group.active && <ObsoleteBadge />}
                        <span className="px-2 py-0.5 text-[10px] bg-white border border-gray-200 text-gray-500 rounded-full font-medium">
                          {skills.length} comp.
                        </span>
                        <MoveButtons
                          onUp={() => handleMoveGroup(group.id, -1)}
                          onDown={() => handleMoveGroup(group.id, 1)}
                          isFirst={groupIdx === 0}
                          isLast={groupIdx === visibleGroups.length - 1}
                        />
                        <NodeActions
                          active={group.active}
                          onEdit={() => openEdit('group', group)}
                          onDeprecate={() => openDeprecate('group', group)}
                          onRestore={() => handleRestore('group', group)}
                        />
                      </div>

                      {/* Skills chips */}
                      {isGroupExpanded && (
                        <div className="px-4 py-3">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {skills.length === 0 && (
                              <p className="text-xs text-gray-400 italic">Aucune compétence dans ce groupe</p>
                            )}
                            {skills.map((skill, skillIdx) => (
                              <span
                                key={skill.id}
                                className="group group/item inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg text-gray-700 hover:border-gray-300 transition-colors"
                              >
                                {!skill.translated ? (
                                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" title="Traduction en attente" />
                                ) : (
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" title="Traduit" />
                                )}
                                <span
                                  className={`cursor-pointer hover:text-[#00244F] ${skill.active ? '' : 'line-through text-gray-400'}`}
                                  onClick={() => openTranslationPanel(skill, 'skills')}
                                >
                                  {skill.nameFr}
                                </span>
                                <MoveButtons
                                  onUp={() => handleMoveSkill(group.id, skill.id, -1)}
                                  onDown={() => handleMoveSkill(group.id, skill.id, 1)}
                                  isFirst={skillIdx === 0}
                                  isLast={skillIdx === skills.length - 1}
                                  orientation="horizontal"
                                />
                                <NodeActions
                                  active={skill.active}
                                  onEdit={() => openEdit('skill', skill)}
                                  onDeprecate={() => openDeprecate('skill', skill)}
                                  onRestore={() => handleRestore('skill', skill)}
                                  compact
                                />
                              </span>
                            ))}
                          </div>

                          {/* Inline add skill */}
                          {addingSkillInGroup === group.id ? (
                            <input
                              ref={skillInputRef}
                              type="text"
                              value={newSkillName}
                              onChange={(e) => setNewSkillName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddSkill(group.id);
                                }
                                if (e.key === 'Escape') {
                                  setAddingSkillInGroup(null);
                                  setNewSkillName('');
                                }
                              }}
                              placeholder="Nom de la compétence + Entrée"
                              autoFocus
                              className="w-full px-3 py-2 text-sm border border-[#00244F]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-gray-50"
                            />
                          ) : (
                            <button
                              onClick={() => { setAddingSkillInGroup(group.id); setNewSkillName(''); }}
                              className="w-full px-3 py-2 text-left text-xs text-gray-400 hover:text-[#00244F] border border-dashed border-gray-200 hover:border-[#00244F]/30 rounded-lg transition-colors"
                            >
                              + Ajouter une compétence
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Inline add group */}
                {addingGroup ? (
                  <div className="border border-dashed border-[#00244F]/30 rounded-xl p-3">
                    <input
                      ref={groupInputRef}
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddGroup();
                        if (e.key === 'Escape') { setAddingGroup(false); setNewGroupName(''); }
                      }}
                      onBlur={() => { if (!newGroupName.trim()) setAddingGroup(false); }}
                      placeholder="Nom du groupe + Entrée"
                      autoFocus
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddingGroup(true); setNewGroupName(''); }}
                    className="w-full px-4 py-3 text-sm text-gray-500 hover:text-[#00244F] border border-dashed border-gray-200 hover:border-[#00244F]/30 rounded-xl transition-colors font-medium"
                  >
                    + Ajouter un groupe de compétences
                  </button>
                )}
              </div>

              {/* Certifications requises ou valorisées pour ce poste */}
              <div className="mt-8 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Certifications ({positionCerts.length})
                </h3>

                {positionCerts.length === 0 && !addingCert && (
                  <p className="text-xs text-gray-400 italic">Aucune certification rattachée à ce poste</p>
                )}

                {positionCerts.length > 0 && (
                  <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
                    {positionCerts.map((cert) => (
                      <div key={cert.certificationId} className="px-4 py-2.5 flex items-center gap-3">
                        <span className={`text-sm flex-1 ${cert.active ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                          {cert.nameFr}
                        </span>
                        {!cert.active && <ObsoleteBadge />}
                        <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={cert.required}
                            onChange={() => handleToggleCertificationRequired(cert)}
                            className="rounded border-gray-300 text-[#00244F] focus:ring-[#00244F]/20"
                          />
                          Obligatoire
                        </label>
                        <button
                          onClick={() => handleUnlinkCertification(cert.certificationId)}
                          title="Détacher du poste"
                          className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {addingCert ? (
                  <select
                    autoFocus
                    defaultValue=""
                    onChange={(e) => handleLinkCertification(e.target.value)}
                    onBlur={() => setAddingCert(false)}
                    className="w-full px-3 py-2 text-sm border border-[#00244F]/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 bg-white"
                  >
                    <option value="">Choisir une certification…</option>
                    {certifications
                      .filter((c) => c.active && !positionCerts.some((pc) => pc.certificationId === c.id))
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.nameFr}</option>
                      ))}
                  </select>
                ) : (
                  <button
                    onClick={() => setAddingCert(true)}
                    className="w-full px-4 py-2.5 text-sm text-gray-500 hover:text-[#00244F] border border-dashed border-gray-200 hover:border-[#00244F]/30 rounded-xl transition-colors font-medium"
                  >
                    + Rattacher une certification
                  </button>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Create sector / subsector modal */}
      {createModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => !creating && setCreateModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">
                {createModal.type === 'sector' ? 'Nouveau secteur' : 'Nouveau sous-secteur'}
              </h3>
              {createModal.type === 'subsector' && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {sectors.find((s) => s.id === createModal.sectorId)?.nameFr}
                </p>
              )}
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {createModal.type === 'sector' ? 'Nom du secteur' : 'Nom du sous-secteur'}
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => handleCreateNameChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && createName.trim()) handleCreateSubmit(); }}
                  autoFocus
                  placeholder={createModal.type === 'sector' ? 'Ex. Aide à domicile' : 'Ex. Drive'}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>

              {createModal.type === 'sector' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={createCode}
                      maxLength={50}
                      onChange={(e) => {
                        setCreateCodeDirty(true);
                        setCreateCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'));
                      }}
                      placeholder="généré automatiquement"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Identifiant technique unique (images, matching). Modifiable avant création.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Visuels</p>
                    <div className="grid grid-cols-2 gap-3">
                      {([
                        { kind: 'logo' as const, label: 'Logo rond', hint: 'Sélecteur de secteur', preview: createLogoPreview },
                        { kind: 'photo' as const, label: 'Photo', hint: 'Offres et fil', preview: createPhotoPreview },
                      ]).map((slot) => (
                        <div key={slot.kind}>
                          <p className="text-xs text-gray-500 mb-1">{slot.label}</p>
                          {slot.preview ? (
                            <div className="relative">
                              <img
                                src={slot.preview}
                                alt={slot.label}
                                className={`w-full h-28 object-cover bg-gray-100 ${slot.kind === 'logo' ? 'rounded-full aspect-square h-28 w-28 mx-auto' : 'rounded-xl'}`}
                              />
                              <button
                                type="button"
                                onClick={() => clearVisual(slot.kind)}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs hover:bg-black/80"
                                title="Retirer"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#00244F]/40 hover:bg-[#00244F]/5 transition-colors text-center px-2">
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                onChange={(e) => pickVisual(slot.kind, e.target.files?.[0])}
                              />
                              <span className="text-lg text-gray-400">+</span>
                              <span className="text-[11px] text-gray-500 mt-0.5">{slot.hint}</span>
                            </label>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">PNG ou JPG, 3 Mo max. Un seul fichier suffit : il sera réutilisé pour les deux usages.</p>
                  </div>
                  <div className="pt-2 border-t border-gray-100 space-y-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Premier sous-secteur (optionnel)
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                      <input
                        type="text"
                        value={createSubName}
                        onChange={(e) => setCreateSubName(e.target.value)}
                        placeholder="Ex. Aide ménagère"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Pôle / catégorie</label>
                      <input
                        type="text"
                        value={createCategory}
                        onChange={(e) => setCreateCategory(e.target.value)}
                        placeholder="Optionnel — ex. Alimentaire"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                      />
                    </div>
                  </div>
                </>
              )}

              {createModal.type === 'subsector' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pôle / catégorie</label>
                  <input
                    type="text"
                    value={createCategory}
                    onChange={(e) => setCreateCategory(e.target.value)}
                    list={`sub-cats-${createModal.sectorId}`}
                    placeholder="Optionnel — ex. Alimentaire"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                  />
                  <datalist id={`sub-cats-${createModal.sectorId}`}>
                    {[...new Set(
                      subsectors
                        .filter((ss) => ss.sectorId === createModal.sectorId && ss.category)
                        .map((ss) => ss.category as string)
                    )].map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setCreateModal(null)}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateSubmit}
                disabled={creating || !createName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {creating ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale d'édition unifiée : nom, code, parent, pôle */}
      {edit && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => !edit.saving && setEdit(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Modifier {NODE_LABEL[edit.kind]}</h3>
              <p className="text-sm text-gray-500 mt-0.5 font-mono">{edit.item.code}</p>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom français</label>
                <input
                  type="text"
                  value={edit.nameFr}
                  onChange={(e) => setEdit({ ...edit, nameFr: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEditSave(); }}
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={edit.code}
                  onChange={(e) => setEdit({ ...edit, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                />
                <p className="text-[11px] text-gray-400 mt-1">
                  Les offres publiées référencent le secteur et le poste par ce code : le changer casse le lien avec l'historique.
                </p>
              </div>

              {edit.kind === 'subsector' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pôle</label>
                    <input
                      type="text"
                      value={edit.category}
                      onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                      placeholder="Ex. Cuisine"
                      list="edit-categories"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                    <datalist id="edit-categories">
                      {Array.from(new Set(subsectors.map((s) => s.category).filter(Boolean))).map((c) => (
                        <option key={c} value={c as string} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secteur</label>
                    <select
                      value={edit.sectorId}
                      onChange={(e) => setEdit({ ...edit, sectorId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      {sectors.map((s) => (
                        <option key={s.id} value={s.id}>{s.active ? s.nameFr : `${s.nameFr} (obsolète)`}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {edit.kind === 'position' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secteur</label>
                    <select
                      value={edit.sectorId}
                      onChange={(e) => setEdit({ ...edit, sectorId: e.target.value, subsectorId: '' })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      {sectors.map((s) => (
                        <option key={s.id} value={s.id}>{s.active ? s.nameFr : `${s.nameFr} (obsolète)`}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sous-secteur</label>
                    <select
                      value={edit.subsectorId}
                      onChange={(e) => setEdit({ ...edit, subsectorId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      <option value="">Aucun (directement sous le secteur)</option>
                      {orderedSubsectors(edit.sectorId).map((ss) => (
                        <option key={ss.id} value={ss.id}>
                          {ss.category ? `${ss.category} – ${ss.nameFr}` : ss.nameFr}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {edit.kind === 'group' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Secteur du poste cible</label>
                    <select
                      value={edit.sectorId}
                      onChange={(e) => setEdit({ ...edit, sectorId: e.target.value, positionId: '' })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      {sectors.map((s) => (
                        <option key={s.id} value={s.id}>{s.nameFr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                    <select
                      value={edit.positionId}
                      onChange={(e) => setEdit({ ...edit, positionId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      <option value="">Choisir un poste…</option>
                      {positions
                        .filter((p) => p.sectorId === edit.sectorId)
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.active ? p.nameFr : `${p.nameFr} (obsolète)`}</option>
                        ))}
                    </select>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Déplacer le groupe réaffecte aussi ses compétences au poste et au secteur cible.
                    </p>
                  </div>
                </>
              )}

              {edit.kind === 'skill' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                    <select
                      value={edit.positionId}
                      onChange={(e) => {
                        const positionId = e.target.value;
                        setEdit({ ...edit, positionId, groupId: '' });
                        setEditGroupOptions([]);
                        if (positionId) {
                          fetchGroupsByPosition(positionId).then(setEditGroupOptions).catch(() => setEditGroupOptions([]));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      <option value="">Choisir un poste…</option>
                      {positions
                        .filter((p) => p.sectorId === edit.sectorId || p.id === edit.positionId)
                        .map((p) => (
                          <option key={p.id} value={p.id}>{p.active ? p.nameFr : `${p.nameFr} (obsolète)`}</option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Groupe de compétences</label>
                    <select
                      value={edit.groupId}
                      onChange={(e) => setEdit({ ...edit, groupId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    >
                      <option value="">Choisir un groupe…</option>
                      {editGroupOptions.map((g) => (
                        <option key={g.id} value={g.id}>{g.active ? g.nameFr : `${g.nameFr} (obsolète)`}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-sm text-gray-600">
                  Statut : {edit.item.active ? 'actif' : 'obsolète'}
                </span>
                {edit.item.active ? (
                  <button
                    onClick={() => { const e = edit; setEdit(null); openDeprecate(e.kind, e.item); }}
                    className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                  >
                    Rendre obsolète
                  </button>
                ) : (
                  <button
                    onClick={() => { const e = edit; setEdit(null); handleRestore(e.kind, e.item); }}
                    className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    Restaurer
                  </button>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setEdit(null)}
                disabled={edit.saving}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleEditSave}
                disabled={edit.saving || !edit.nameFr.trim() || !edit.code.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {edit.saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Récapitulatif d'usage avant mise en obsolescence */}
      {deprecate && (
        <DeprecateDialog
          table={NODE_TABLE[deprecate.kind]}
          id={deprecate.item.id}
          name={deprecate.item.nameFr}
          label={NODE_LABEL[deprecate.kind]}
          onCancel={() => setDeprecate(null)}
          onConfirmed={async () => {
            await reloadTree();
            if (deprecate.kind === 'group' || deprecate.kind === 'skill') await refreshPositionDetail();
            setDeprecate(null);
          }}
        />
      )}

      {/* Translation detail modal */}
      {translationItem && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setTranslationItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold text-gray-900">Traductions</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                🇫🇷 {translationItem.item.nameFr}
              </p>
            </div>
            <div className="p-6 space-y-3">
              {TRANSLATION_LANGS.map(({ key, label }) => {
                const isLocked = translationItem.item.translationLocked?.[key] || false;
                const currentValue = translationEdits[key] || '';
                return (
                  <div key={key} className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-[10px] font-medium text-gray-500 uppercase mb-0.5">
                        {label} ({key})
                      </label>
                      <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => setTranslationEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTranslation(key); }}
                        dir={key === 'ar' ? 'rtl' : 'ltr'}
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 ${
                          currentValue ? 'border-gray-200 bg-white' : 'border-orange-200 bg-orange-50'
                        }`}
                        placeholder={`Traduction ${label}...`}
                      />
                    </div>
                    <div className="flex flex-col items-center gap-1 pt-4">
                      <button
                        onClick={() => handleSaveTranslation(key)}
                        disabled={translationSaving}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
                        title="Sauvegarder + verrouiller"
                      >
                        💾
                      </button>
                      <button
                        onClick={() => handleToggleLock(key)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isLocked ? 'text-amber-600 bg-amber-50 hover:bg-amber-100' : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={isLocked ? 'Déverrouiller (la prochaine traduction auto écrasera)' : 'Verrouiller (protège des batch)'}
                      >
                        {isLocked ? '🔒' : '🔓'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50 sticky bottom-0">
              <p className="text-[10px] text-gray-400">
                🔒 = protégé des traductions batch automatiques
              </p>
              <button onClick={() => setTranslationItem(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <ImportWizard
          sectors={sectors}
          subsectors={subsectors}
          onClose={() => setShowImportModal(false)}
          onImported={handleImported}
        />
      )}

      {showMissingTranslations && (
        <MissingTranslationsModal
          onClose={() => setShowMissingTranslations(false)}
          onDone={() => { reloadTree(); refreshPositionDetail(); }}
        />
      )}
    </div>
  );
};

export default Referentials;
